import { useMemo } from "react";
import { Link } from "react-router-dom";

import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import {
  balanceBlockerMessage,
  compactCents,
  formatBps,
  formatCivilDate,
  formatCompetenceShort,
  formatFreshness,
} from "../../utils/directorFormat";
import { companyQuery } from "./module1Utils";

const STATUS_VALUE = {
  blocked: "Bloqueado",
  empty: "Sem dados",
  error: "Indisponível",
  next_phase: "Próxima fase",
};

const REASON_MESSAGES = {
  no_data: "Ainda não há dados financeiros publicados para este portfólio.",
  source_restricted: "A fonte de parcelas a receber está sem permissão de leitura para esta empresa.",
  planned_unavailable: "Uma obra usa Planejamento empresarial sem composição do planejado.",
};

const DEFAULT_REASON = {
  blocked: "A leitura está bloqueada por uma limitação da fonte.",
  empty: "Ainda não há dados neste recorte.",
  error: "Não foi possível calcular este indicador agora. Abra a tela para tentar de novo.",
};

const plural = (value, singular, pluralForm) => `${value} ${value === 1 ? singular : pluralForm}`;

const joinNames = (names) => {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} e ${names.at(-1)}`;
};

const reasonFor = (card) => (
  REASON_MESSAGES[card?.reasonCode] || DEFAULT_REASON[card?.availability] || DEFAULT_REASON.error
);

const receivablesCard = (card, data) => {
  const summary = card.summary || {};
  const months = card.horizon?.months || data?.horizon?.months || 12;
  const uncovered = Number.isSafeInteger(summary.uncoveredMonths) ? summary.uncoveredMonths : 0;
  const covers = Number.isSafeInteger(summary.coverageBps) && summary.coverageBps >= 10000;
  const worst = summary.worstMonth ? `, o pior em ${formatCompetenceShort(summary.worstMonth)}` : "";
  let sentence;
  if (covers && uncovered === 0) {
    sentence = `As entradas previstas cobrem os compromissos dos próximos ${months} meses, sem mês descoberto.`;
  } else if (covers) {
    sentence = `As entradas previstas cobrem os compromissos dos próximos ${months} meses, mas ${plural(uncovered, "mês fica descoberto", "meses ficam descobertos")}${worst}.`;
  } else {
    sentence = `As entradas previstas não cobrem os compromissos dos próximos ${months} meses: ${plural(uncovered, "mês fica descoberto", "meses ficam descobertos")}${worst}.`;
  }
  const commitments = Number.isSafeInteger(summary.committedCents) && Number.isSafeInteger(summary.plannedCents)
    ? summary.committedCents + summary.plannedCents
    : null;
  return {
    value: formatBps(summary.coverageBps),
    tone: covers ? "green" : "pressure",
    sentence,
    stats: [
      ["Entradas previstas", compactCents(summary.receivableCents)],
      ["Compromissos", compactCents(commitments)],
    ],
  };
};

const capitalCard = (card) => {
  const summary = card.summary || {};
  const needing = (summary.poolsNeedingFunds || []).map((pool) => pool.label || pool.poolingGroupId);
  const critical = summary.firstNegativeMonth || summary.firstBelowBufferMonth;
  const criticalPool = summary.criticalPoolingGroup?.label;
  const sentence = summary.fundingGapCents > 0
    ? `Aporte para manter o colchão${needing.length ? ` de ${joinNames(needing)}` : ""}.${critical
      ? ` O primeiro aperto é em ${formatCompetenceShort(critical)}${criticalPool ? `, em ${criticalPool}` : ""}.`
      : ""}`
    : "Nenhum caixa precisa de aporte para manter o colchão no período.";
  return {
    value: compactCents(summary.fundingGapCents),
    tone: summary.fundingGapCents > 0 ? "pressure" : "green",
    sentence,
    stats: [
      [
        "Menor saldo",
        `${compactCents(summary.minimumBalanceCents)}${summary.minimumBalancePoolingGroup?.label
          ? ` em ${summary.minimumBalancePoolingGroup.label}`
          : ""}`,
      ],
      card.consumption
        ? consumptionStat(card.consumption)
        : ["Saldos informados em", card.openingAsOf ? formatCivilDate(String(card.openingAsOf).slice(0, 10)) : "—"],
    ],
  };
};

const consumptionStat = (consumption) => [
  "Capital que as obras consomem",
  consumption.peakCapitalCents > 0
    ? `${compactCents(consumption.peakCapitalCents)}${consumption.peakMonth ? ` em ${formatCompetenceShort(consumption.peakMonth)}` : ""}`
    : "Não fica negativo",
];

// Sem saldo inicial o aporte não sai, mas o capital que as obras consomem sim.
const consumptionCard = (consumption) => {
  const peak = consumption.peakCapitalCents;
  const top = consumption.topProject;
  const sentence = peak > 0
    ? `No pior momento, as obras consomem ${compactCents(peak)} do seu capital${consumption.peakMonth
      ? `, em ${formatCompetenceShort(consumption.peakMonth)}`
      : ""}${top?.name ? `. A que mais consome é ${top.name}` : ""}. O aporte no caixa depende do saldo inicial.`
    : "As obras se pagam com o que recebem: o acumulado não fica negativo. O aporte no caixa depende do saldo inicial.";
  return {
    value: peak > 0 ? compactCents(peak) : "R$ 0",
    tone: peak > 0 ? "orange" : "green",
    sentence,
    stats: [
      [
        "Pico até hoje",
        consumption.realizedPeakCapitalCents > 0
          ? `${compactCents(consumption.realizedPeakCapitalCents)}${consumption.realizedPeakMonth
            ? ` em ${formatCompetenceShort(consumption.realizedPeakMonth)}`
            : ""}`
          : "Não ficou negativo",
      ],
      ["Histórico carregado em", consumption.lastFullLoadAt ? formatFreshness(consumption.lastFullLoadAt) : "—"],
    ],
  };
};

const delinquencyCard = (card) => {
  const summary = card.summary || {};
  const customers = Number.isSafeInteger(summary.customersOverdue) ? summary.customersOverdue : 0;
  const sentence = customers > 0
    ? `Vencido e não pago por ${plural(customers, "cliente", "clientes")}. ${summary.over90Cents > 0
      ? `${compactCents(summary.over90Cents)} está atrasado há mais de 90 dias.`
      : "Nada está atrasado há mais de 90 dias."}${card.topProject?.name
      ? ` A maior parte do vencido está em ${card.topProject.name}.`
      : ""}`
    : "Nenhum cliente com parcela vencida.";
  return {
    value: compactCents(summary.overdueCents),
    tone: summary.overdueCents > 0 ? "orange" : "green",
    sentence,
    stats: [
      ["Do que vence em 12 meses", formatBps(summary.overdueShareBps)],
      ["Recuperado no mês", compactCents(summary.recoveredThisMonthCents)],
    ],
  };
};

const CARDS = [
  {
    key: "receivablesCommitments",
    question: "O que entra cobre o que já assumi?",
    capability: "director.cashflow.read",
    path: "/dashboard/director/receivables-commitments",
    action: "Ver mês a mês e por obra",
    build: receivablesCard,
  },
  {
    key: "margin",
    question: "Minha margem está se mantendo?",
    capability: null,
    path: null,
    action: null,
    build: null,
  },
  {
    key: "capital",
    question: "Quanto capital vou precisar e quando?",
    capability: "director.liquidity.read",
    path: "/dashboard/director/capital",
    action: "Ver a curva por obra e o caixa",
    build: capitalCard,
  },
  {
    key: "delinquency",
    question: "Quem está me devendo?",
    capability: "receivables.details.read",
    path: "/dashboard/director/delinquency",
    action: "Ver clientes em atraso",
    build: delinquencyCard,
  },
];

const OwnerCardBody = ({ definition, content, availability }) => <>
  <span>{definition.question}</span>
  {availability !== "available" && <em className="director-owner-card__status" data-status={availability}>
    {STATUS_VALUE[availability] || "Indisponível"}
  </em>}
  {content.value && <strong>{content.value}</strong>}
  <p className="director-owner-card__sentence">{content.sentence}</p>
  {content.stats?.length > 0 && <dl className="director-owner-card__stats">
    {content.stats.map(([label, value]) => <div key={label}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>)}
  </dl>}
  {definition.action && <small className="director-owner-card__action">{definition.action}</small>}
</>;

const OwnerView = () => {
  const { selectedCompanyId, accessByCompany = {} } = useDirectorContext();
  const query = useMemo(() => ({ scenario: "base" }), []);
  const surface = useDirectorSurface("owner-view", query);
  const { data } = surface;
  const access = accessByCompany[selectedCompanyId] || {};
  const capabilities = access.capabilities || [];

  const cards = CARDS.map((definition) => {
    const card = data?.cards?.[definition.key] || null;
    let availability = definition.key === "margin" ? "next_phase" : (card?.availability || "error");
    if (availability === "forbidden") return null;
    if (definition.key === "capital" && availability !== "available" && card?.consumption) {
      if (definition.capability && !capabilities.includes(definition.capability)) return null;
      availability = "available";
      return { definition, availability, content: consumptionCard(card.consumption) };
    }
    if (definition.capability && !capabilities.includes(definition.capability)) return null;
    let content;
    if (availability === "next_phase") {
      content = { sentence: "A margem por obra entra na próxima fase da Diretoria, com a tendência de custo do Ampli Obras." };
    } else if (availability === "available") {
      content = definition.build(card, data);
    } else if (definition.key === "capital" && availability === "blocked" && card?.blockers?.length) {
      content = { sentence: card.blockers.map(balanceBlockerMessage).join(" ") };
    } else {
      content = { sentence: reasonFor(card) };
    }
    return { definition, availability, content };
  }).filter(Boolean);

  return <ModuleShell
    eyebrow={`Diretoria · ${data?.portfolioName || data?.companyName || access.companyName || "portfólio"}`}
    title="Visão do dono"
    description="As perguntas que o dono faz, respondidas com o ERP de cada obra."
    badge={`Próximos ${data?.horizon?.months || 12} meses`}
    qualitySources={[surface]}
    showProjectFilter={false}
  >
    <ModuleState {...surface} onRetry={surface.reload} />
    {data && <>
      <section className="director-owner-grid" aria-label="Perguntas do dono">
        {cards.map(({ definition, availability, content }) => {
          const className = `director-module1__metric director-module1__metric--${availability === "available" ? content.tone : "neutral"} director-owner-card`;
          const body = <OwnerCardBody definition={definition} content={content} availability={availability} />;
          return definition.path
            ? <Link
              className={className}
              data-availability={availability}
              key={definition.key}
              to={`${definition.path}${companyQuery(selectedCompanyId)}`}
            >
              {body}
            </Link>
            : <article className={className} data-availability={availability} key={definition.key}>{body}</article>;
        })}
      </section>
      <footer className="director-freshness">
        Fontes: ERP da obra (títulos a receber e a pagar) e saldos informados na configuração.
        {data.generatedAt ? ` Atualizado em ${formatFreshness(data.generatedAt)}.` : ""}
      </footer>
    </>}
  </ModuleShell>;
};

export default OwnerView;
